"use client";

import {
  createContext,
  useContext,
  useCallback,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import type { UserProfile, PlanTier } from "@/types/user";

interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  role: string | null;
}

interface MeUser {
  id: string;
  clerk_id: string;
  user_handle: string | null;
  email: string;
  display_name: string | null;
  photo_url: string | null;
  plan: string;
  role: string | null;
  scan_count: number | null;
  scan_reset_date: string | null;
  plan_start_date: string | null;
  pentest_count: number | null;
  pentest_reset_month: string | null;
  pentest_pending: boolean | null;
  pentest_pending_at: string | null;
  detailed_report_credits: number;
  invite_code: string | null;
  email_verified_at: string | null;
  last_login_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface SelfMeResponse {
  user: MeUser;
}

interface AuthContextValue {
  user: AuthUser | null;
  profile: UserProfile | null;
  loading: boolean;
  signOut: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function fetchMe(): Promise<MeUser | null> {
  try {
    const res = await fetch("/api/auth/me", {
      cache: "no-store",
      credentials: "include",
    });
    if (!res.ok) return null;
    const data = (await res.json()) as SelfMeResponse;
    return data.user;
  } catch {
    return null;
  }
}

function buildProfile(me: MeUser): UserProfile {
  return {
    uid: me.clerk_id,
    email: me.email,
    displayName: me.display_name ?? "",
    photoURL: me.photo_url,
    plan: (me.plan ?? "free") as PlanTier,
    scanCount: me.scan_count ?? 0,
    scanResetDate: me.scan_reset_date ?? "",
    planStartDate: me.plan_start_date ?? "",
    createdAt: me.created_at ?? "",
    updatedAt: me.updated_at ?? "",
    pentestCount: me.pentest_count ?? 0,
    pentestResetMonth: me.pentest_reset_month ?? undefined,
    pentestPending: me.pentest_pending ?? false,
    pentestPendingAt: me.pentest_pending_at ?? undefined,
    detailedReportCredits: me.detailed_report_credits ?? 0,
    inviteCode: me.invite_code ?? undefined,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<MeUser | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    fetchMe().then((u) => {
      if (!cancelled) setMe(u);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const user: AuthUser | null = me
    ? {
        uid: me.clerk_id,
        email: me.email ?? null,
        displayName: me.display_name,
        photoURL: me.photo_url,
        role: me.role,
      }
    : null;

  const profile: UserProfile | null = me ? buildProfile(me) : null;

  const signOut = useCallback(async () => {
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // ignore
    }
    window.location.href = "/";
  }, []);

  const getIdToken = useCallback(async (): Promise<string | null> => {
    if (process.env.NEXT_PUBLIC_ADMIN_MODE === "1") return "admin-mode";
    return me ? "self-session" : null;
  }, [me]);

  const refreshProfile = useCallback(async () => {
    const fresh = await fetchMe();
    setMe(fresh);
  }, []);

  const loading = me === undefined;

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        loading,
        signOut,
        getIdToken,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
